import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiCalendar, FiDollarSign, FiUpload } from "react-icons/fi";
import styles from "./Styles/TaskInput.module.css";
import useElapsed from "./useElapsed";
import useRunningSync from "./useRunningSync";
import { postTasksApi, startRunningApi, stopRunningApi } from "../../Redux/Tasks/Task.actions";
import { favoriteProjectApi, unfavoriteProjectApi } from "../../Redux/Projects/Project.actions";
import { projectOption } from "../common/projectLabel";
import activeProjects from "../common/activeProjects";
import AutocompleteSelect from "../common/AutocompleteSelect";
import Tooltip from "../common/Tooltip";
import { formatHMS } from "../Reports/reportFormat";
import {
  durationDigits,
  formatDuration,
  ghostDuration,
  localDay,
  localTime,
  maskDuration,
  parseDurationDigits,
  placeEntry,
  startFrom,
} from "./parseDuration";
import { useI18n } from "../../i18n/I18nContext";

const TaskInput = ({ isAdminOrOwner, onImportClick }) => {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [projectId, setProjectId] = useState("");
  const [allDayDate, setAllDayDate] = useState("");
  // Whether the next created entry counts as billable. Sticky on purpose:
  // it keeps its value between entries, like the project selection does.
  const [billable, setBillable] = useState(true);
  // Which way time is logged: running the timer, or typing an amount. Every
  // visit starts on the timer; Manual only lasts for this page visit.
  const [mode, setMode] = useState("timer");
  // Manual mode: the digits typed for the amount (up to four, read as HH:MM
  // filling left to right), and whether they failed to read when the user
  // left the field. By default the entry ends now; the optional start
  // fields (hidden until asked for) pin where it begins instead.
  const [duration, setDuration] = useState("");
  const [durationInvalid, setDurationInvalid] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [startDay, setStartDay] = useState("");
  const [startTime, setStartTime] = useState("");
  const allDayInputRef = useRef(null);
  // Set while a save, start or stop is on its way to the server, so a quick
  // double-click or a second Enter can't send it twice. The ref is the guard
  // (two clicks in the same tick both see it); the state only greys out the
  // button so the row looks busy.
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const { running } = useSelector((state) => state.tasks);
  const { user } = useSelector((state) => state.auth);
  const { currentOrgId } = useSelector((state) => state.organizations);
  const { projects, favoriteIds } = useSelector((state) => state.projects);
  const { clients } = useSelector((state) => state.clients);
  const dispatch = useDispatch();
  const elapsed = useElapsed(running ? running.startedAt : null);

  // The timer lives on the server, so the page has to ask what's running
  // rather than remember it: after a reload, a logout or a move to another
  // device the tracker picks the entry back up where it actually is. It keeps
  // asking, too, since it can be stopped somewhere else while this sits open.
  useRunningSync(currentOrgId, user.token);

  // Adopt whatever is running into the form: on load, and when Continue on a
  // past entry (issue #3) starts a timer with that entry's details. Keyed on
  // the entry's id rather than the object, because the resync hands back a
  // fresh object for the same timer every 30 seconds and re-adopting it would
  // wipe out a description being typed.
  const adoptedId = useRef(null);
  useEffect(() => {
    if (!running) {
      // Stopped somewhere else: the description went with the finished entry,
      // so the box empties exactly like it does after stopping here. The
      // project and the billable toggle stay, they're sticky between entries.
      if (adoptedId.current) {
        adoptedId.current = null;
        setName("");
      }
      return;
    }
    if (adoptedId.current === running.id) return;
    adoptedId.current = running.id;
    setName(running.text || "");
    setProjectId(running.projectId || "");
    setBillable(running.billable !== false);
  }, [running]);

  // Stars or unstars a project for this user, pinning it to the top of the
  // picker on every device (issue #5). Only this picker offers it.
  const toggleFavorite = (id) => {
    const toggle = favoriteIds.includes(id) ? unfavoriteProjectApi : favoriteProjectApi;
    dispatch(toggle(currentOrgId, id, user.token));
  };

  // handleAllDayDate creates an all-day time entry on the picked day for the
  // currently selected project, as soon as a date is chosen - no separate
  // "create" click needed (ai-instruct-60).
  const handleAllDayDate = (e) => {
    const day = e.target.value;
    setAllDayDate(day);
    if (!day) return;
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    const taskObj = {
      text: name || project.name,
      day,
      allDay: true,
      billable,
      clientId: project.clientId,
      projectId: project.id,
    };
    dispatch(postTasksApi(taskObj, currentOrgId, user.token));
    setAllDayDate("");
  };

  // Opens the (visually hidden) native date input's picker from the icon
  // button - showPicker() is the modern, no-visible-input way to trigger it;
  // .click() is the fallback for browsers that don't support it yet.
  const openAllDayPicker = () => {
    const el = allDayInputRef.current;
    if (!el) return;
    if (el.showPicker) {
      el.showPicker();
    } else {
      el.click();
    }
  };

  // A running timer (started elsewhere, or by Continue) always shows the
  // timer controls, or there'd be no way to stop it from here.
  const manual = mode === "manual" && !running;
  const durationSeconds = parseDurationDigits(duration);
  const customStart = startOpen ? startFrom(startDay, startTime) : null;
  const canAdd = !!projectId && durationSeconds !== null && (!startOpen || !!customStart);

  // Opening the start fields always begins from the current date and time;
  // closing them goes back to entries that end now.
  const toggleStart = () => {
    if (!startOpen) {
      const now = new Date();
      now.setSeconds(0);
      setStartDay(localDay(now));
      setStartTime(localTime(now));
    }
    setStartOpen((open) => !open);
  };

  // On leaving the field, a valid amount is shown in full ("23" becomes
  // "23:00") so the user sees what will be logged; anything else is flagged.
  const handleDurationBlur = () => {
    if (durationSeconds !== null) {
      setDuration(durationDigits(formatDuration(durationSeconds)));
    } else {
      setDurationInvalid(duration !== "");
    }
  };

  // Runs one request at a time: anything submitted while another is out is
  // dropped, and the guard lifts once it settles, whether it worked or not.
  const once = async (request) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await request();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  // Logs the typed amount as a finished entry: from the start fields when
  // they're open, otherwise ending now. One that crosses midnight is saved
  // as two entries (placeEntry splits it), one after the other, stopping at
  // the first failure. The description and the amount only clear once every
  // part is saved, so a failed request (which already shows a toast) doesn't
  // lose what was typed.
  const handleAdd = () => {
    if (!canAdd) return;
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    return once(() => addEntry(project));
  };

  const addEntry = async (project) => {
    const start = customStart || new Date(Date.now() - durationSeconds * 1000);
    for (const part of placeEntry(start, durationSeconds)) {
      const saved = await dispatch(
        postTasksApi(
          {
            text: name || project.name,
            ...part,
            allDay: false,
            billable,
            clientId: project.clientId,
            projectId: project.id,
          },
          currentOrgId,
          user.token
        )
      );
      if (!saved) return;
    }
    setName("");
    setDuration("");
    setDurationInvalid(false);
    // The next block picks up where this one ended, so several logged in a
    // row line up without overlapping.
    if (customStart) {
      const end = new Date(customStart.getTime() + durationSeconds * 1000);
      setStartDay(localDay(end));
      setStartTime(localTime(end));
    }
  };

  const handleSubmit = () => {
    if (running) {
      // The description may have been edited while the timer ran, so it goes
      // along as an override; the project stays as it was started.
      setName("");
      return once(() => dispatch(stopRunningApi({ text: name || undefined, billable }, currentOrgId, user.token)));
    }
    const project = projects.find((p) => p.id === projectId);
    if (!project) {
      return;
    }
    return once(() =>
      dispatch(
        startRunningApi(
          {
            text: name || project.name,
            clientId: project.clientId,
            projectId: project.id,
            billable,
            // The instant and zone come from the browser: the elapsed time is
            // measured against this, and the entry's day is the user's day.
            startedAt: new Date().toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          currentOrgId,
          user.token
        )
      )
    );
  };

  return (
    <div className={styles.Task}>
      <div className={styles.Desc}>
        <input
          className={styles.textInput}
          type="text"
          placeholder={t("timeTracker.whatAreYouWorkingOn")}
          title={t("timeTracker.whatAreYouWorkingOn")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <AutocompleteSelect
          className={styles.Projects}
          label={t("timeTracker.project")}
          placeholder={t("timeTracker.project")}
          options={activeProjects(projects).map((p) => ({ ...projectOption(p, clients), favorite: favoriteIds.includes(p.id) }))}
          value={projectId}
          onChange={setProjectId}
          onToggleFavorite={toggleFavorite}
          disabled={!!running}
        />
        <div className={styles.Timer}>
          {/* Both ways of logging, named, with the one in use highlighted. A
              running timer locks it on Timer. */}
          <div className={styles.ModeSwitch} role="group" aria-label={t("timeTracker.logMode")}>
            {["timer", "manual"].map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={manual === (m === "manual")}
                onClick={() => setMode(m)}
                disabled={!!running}
              >
                {m === "manual" ? t("timeTracker.modeManual") : t("timeTracker.modeTimer")}
              </button>
            ))}
          </div>
          {manual ? (
            <>
              {/* The dim overlay shows the rest of the "00:00" template after
                  what was typed; the input underneath keeps the typed text,
                  so the caret, backspace and screen readers work as usual. */}
              <span className={styles.DurationField}>
                <input
                  className={`${styles.DurationInput} ${durationInvalid ? styles.Invalid : ""}`}
                  type="text"
                  inputMode="numeric"
                  title={t("timeTracker.durationHint")}
                  aria-label={t("timeTracker.duration")}
                  aria-invalid={durationInvalid}
                  value={maskDuration(duration)}
                  onChange={(e) => {
                    // Only digits are kept; the colon is added back when shown,
                    // so deleting past it just removes the digit before it.
                    setDuration(durationDigits(e.target.value));
                    setDurationInvalid(false);
                  }}
                  onBlur={handleDurationBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAdd();
                  }}
                />
                <span className={styles.DurationGhost} aria-hidden="true">
                  <span className={styles.DurationTyped}>{maskDuration(duration)}</span>
                  <span className={styles.DurationRest}>{ghostDuration(duration)}</span>
                </span>
              </span>
              <Tooltip label={startOpen ? t("timeTracker.endNow") : t("timeTracker.setStart")} position="bottom">
                <button
                  type="button"
                  className={`${styles.AllDayIconBtn} ${startOpen ? styles.Pressed : ""}`}
                  onClick={toggleStart}
                  aria-pressed={startOpen}
                  aria-label={t("timeTracker.setStart")}
                >
                  <FiCalendar fontSize="16px" />
                </button>
              </Tooltip>
              {startOpen && (
                <span className={styles.StartFields}>
                  <input
                    className={styles.DayInput}
                    type="date"
                    aria-label={t("timeTracker.startDate")}
                    title={t("timeTracker.startDate")}
                    value={startDay}
                    onChange={(e) => setStartDay(e.target.value)}
                  />
                  <input
                    className={styles.TimeInput}
                    type="time"
                    aria-label={t("timeTracker.startTime")}
                    title={t("timeTracker.startTime")}
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </span>
              )}
              <button
                className={styles.Blue}
                onClick={handleAdd}
                disabled={!canAdd || busy}
                title={t("timeTracker.addEntry")}
              >
                {t("timeTracker.add")}
              </button>
            </>
          ) : (
            <>
              <span className={styles.clock} title={t("timeTracker.elapsedTime")}>
                {formatHMS(elapsed)}
              </span>
              <button
                className={running ? styles.Red : styles.Blue}
                onClick={handleSubmit}
                disabled={!projectId || busy}
                title={running ? t("timeTracker.stopTimer") : t("timeTracker.startTimer")}
              >
                {running ? t("timeTracker.stop") : t("timeTracker.start")}
              </button>
            </>
          )}
          {isAdminOrOwner && (
            <Tooltip label={t("timeTracker.importCsvTitle")} position="bottom">
              <button
                type="button"
                className={styles.AllDayIconBtn}
                onClick={onImportClick}
                aria-label={t("timeTracker.importCsvTitle")}
              >
                <FiUpload fontSize="16px" />
              </button>
            </Tooltip>
          )}
          {/* Manual mode has its own start date, so the all-day picker steps aside. */}
          {!manual && (
            <>
              <input
                ref={allDayInputRef}
                type="date"
                className={styles.AllDayPickerHidden}
                value={allDayDate}
                onChange={handleAllDayDate}
                disabled={!projectId || !!running}
                aria-hidden="true"
                tabIndex={-1}
              />
              <Tooltip label={t("timeTracker.allDay")} position="bottom">
                <button
                  type="button"
                  className={styles.AllDayIconBtn}
                  onClick={openAllDayPicker}
                  disabled={!projectId || !!running}
                  aria-label={t("timeTracker.allDay")}
                >
                  <FiCalendar fontSize="16px" />
                </button>
              </Tooltip>
            </>
          )}
          <Tooltip label={billable ? t("timeTracker.billable") : t("timeTracker.nonBillable")} position="bottom">
            <button
              type="button"
              className={styles.AllDayIconBtn}
              onClick={() => setBillable((b) => !b)}
              aria-pressed={billable}
              aria-label={t("timeTracker.markBillable")}
              style={billable ? undefined : { color: "#e46962" }}
            >
              <FiDollarSign fontSize="16px" />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default TaskInput;
